import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import type { BetaAccessRequestResponse } from '@/lib/api';

interface AdminRequestsTableProps {
  requests: BetaAccessRequestResponse[];
  isLoading: boolean;
  onSelectRequest: (request: BetaAccessRequestResponse) => void;
}

function getStatusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'accepted':
      return 'default';
    case 'rejected':
      return 'destructive';
    default:
      return 'secondary';
  }
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function AdminRequestsTable({
  requests,
  isLoading,
  onSelectRequest,
}: AdminRequestsTableProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (requests.length === 0) {
    return <div className="text-center py-8 text-muted-foreground">No requests found.</div>;
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Submitted</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {requests.map((req) => (
            <TableRow
              key={req.requestId}
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => onSelectRequest(req)}
            >
              <TableCell className="font-medium">{req.name}</TableCell>
              <TableCell>{req.email}</TableCell>
              <TableCell>
                <Badge variant={getStatusVariant(req.status)}>{req.status}</Badge>
              </TableCell>
              <TableCell>{formatDate(req.createdAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
